const recommendations = {
  vata: {
    name: 'Vata',
    element: 'Air + Space',
    qualities: ['Light', 'Dry', 'Cold', 'Subtle', 'Mobile'],
    description: 'Vata governs movement and communication in the body. When balanced, you feel creative, energetic, and adaptable. When imbalanced, you may experience anxiety, restlessness, and digestive issues.',
    balancedTraits: [
      'Creative and artistic',
      'Energetic and enthusiastic',
      'Quick thinking and adaptable',
      'Good circulation',
      'Regular elimination'
    ],
    imbalancedTraits: [
      'Anxiety and worry',
      'Insomnia and restlessness',
      'Dry skin and constipation',
      'Scattered thinking',
      'Cold hands and feet'
    ],
    recommendedTreatments: ['abhyanga', 'shirodhara', 'basti'],
    dietGuidelines: {
      favor: [
        'Warm, cooked foods',
        'Sweet, sour, and salty tastes',
        'Nourishing oils and ghee',
        'Regular meal times',
        'Warm beverages'
      ],
      avoid: [
        'Cold, raw foods',
        'Excessive bitter, pungent tastes',
        'Irregular eating',
        'Too much caffeine',
        'Dry, light foods'
      ]
    },
    lifestyle: [
      'Maintain regular daily routines',
      'Get adequate rest (7-8 hours)',
      'Practice gentle, grounding exercises',
      'Stay warm and avoid excessive cold',
      'Practice meditation and breathing exercises'
    ],
    seasonalAdvice: {
      fall: 'Prime Vata season - focus on grounding routines and warm foods',
      winter: 'Continue warm, nourishing practices',
      spring: 'Gentle detox with warm, light foods',
      summer: 'Stay hydrated and avoid overheating'
    }
  },
  pitta: {
    name: 'Pitta',
    element: 'Fire + Water',
    qualities: ['Hot', 'Light', 'Sharp', 'Oily', 'Mobile'],
    description: 'Pitta governs transformation and metabolism in the body. When balanced, you feel focused, confident, and have strong digestion. When imbalanced, you may experience anger, inflammation, and digestive heat.',
    balancedTraits: [
      'Sharp intellect and focus',
      'Strong digestion',
      'Natural leadership',
      'Good circulation',
      'Lustrous skin and hair'
    ],
    imbalancedTraits: [
      'Anger and irritability',
      'Skin rashes and inflammation',
      'Heartburn and acidity',
      'Excessive competitiveness',
      'Sensitivity to heat'
    ],
    recommendedTreatments: ['ayurvedic_facial', 'shirodhara', 'abhyanga'],
    dietGuidelines: {
      favor: [
        'Cool, fresh foods',
        'Sweet, bitter, and astringent tastes',
        'Fresh fruits and vegetables',
        'Cooling spices like coriander',
        'Room temperature or cool beverages'
      ],
      avoid: [
        'Hot, spicy foods',
        'Excessive sour and salty foods',
        'Fried and oily foods',
        'Alcohol and caffeine',
        'Eating when angry or stressed'
      ]
    },
    lifestyle: [
      'Avoid excessive heat and sun',
      'Practice moderation in activities',
      'Engage in cooling exercises like swimming',
      'Take time for relaxation and fun',
      'Practice patience and avoid overwork'
    ],
    seasonalAdvice: {
      summer: 'Prime Pitta season - stay cool and avoid excess heat',
      fall: 'Transition gently with cooling foods',
      winter: 'Maintain warmth without overheating',
      spring: 'Good time for gentle detox'
    }
  },
  kapha: {
    name: 'Kapha',
    element: 'Earth + Water',
    qualities: ['Heavy', 'Slow', 'Cold', 'Oily', 'Stable'],
    description: 'Kapha governs structure and lubrication in the body. When balanced, you feel calm, strong, and have good immunity. When imbalanced, you may experience weight gain, congestion, and lethargy.',
    balancedTraits: [
      'Calm and peaceful nature',
      'Strong immunity',
      'Stable emotions',
      'Good physical strength',
      'Healthy, lustrous skin'
    ],
    imbalancedTraits: [
      'Weight gain and sluggishness',
      'Congestion and mucus',
      'Depression and attachment',
      'Slow digestion',
      'Difficulty with change'
    ],
    recommendedTreatments: ['udvarthana', 'abhyanga', 'nasya'],
    dietGuidelines: {
      favor: [
        'Light, warm foods',
        'Pungent, bitter, and astringent tastes',
        'Warming spices like ginger',
        'Smaller, frequent meals',
        'Warm beverages'
      ],
      avoid: [
        'Heavy, oily foods',
        'Excessive sweet and salty foods',
        'Cold drinks and ice cream',
        'Overeating',
        'Too much dairy'
      ]
    },
    lifestyle: [
      'Engage in regular, vigorous exercise',
      'Wake up early and avoid daytime naps',
      'Seek variety and stimulation',
      'Practice energizing breathing exercises',
      'Maintain social connections'
    ],
    seasonalAdvice: {
      spring: 'Prime Kapha season - focus on detox and light foods',
      summer: 'Moderate activity and stay cool',
      fall: 'Prepare for winter with warming practices',
      winter: 'Stay active and avoid excessive heaviness'
    }
  }
};

// Service recommendations based on dosha
const serviceRecommendations = {
  vata: {
    primary: ['abhyanga', 'shirodhara', 'basti'],
    secondary: ['shiro_abhyanga', 'pada_abhyanga'],
    avoid: []
  },
  pitta: {
    primary: ['ayurvedic_facial', 'shirodhara', 'netra_basti'],
    secondary: ['abhyanga', 'shiro_abhyanga'],
    avoid: ['nasya'] // can aggravate pitta
  },
  kapha: {
    primary: ['nasya', 'abhyanga', 'kati_abhyanga'],
    secondary: ['pada_abhyanga', 'ayurvedic_facial'],
    avoid: []
  }
};

// Constitution-specific recommendations for dual doshas
const dualDoshaRecommendations = {
  'vata-pitta': {
    focus: 'Balance air and fire elements',
    priority: 'Calm Vata first, then cool Pitta',
    treatments: ['abhyanga', 'shirodhara', 'ayurvedic_facial'],
    lifestyle: 'Regular routines with cooling practices'
  },
  'vata-kapha': {
    focus: 'Balance air and earth elements',
    priority: 'Warm and energize without aggravating Vata',
    treatments: ['abhyanga', 'nasya', 'shiro_abhyanga'],
    lifestyle: 'Gentle stimulation with grounding practices'
  },
  'pitta-kapha': {
    focus: 'Balance fire and earth elements',
    priority: 'Cool Pitta while stimulating Kapha',
    treatments: ['ayurvedic_facial', 'abhyanga', 'nasya'],
    lifestyle: 'Moderate activity with cooling foods'
  },
  'vata-pitta-kapha': {
    focus: 'Tridoshic constitution - maintain overall balance',
    priority: 'Seasonal awareness and gentle balancing',
    treatments: ['abhyanga', 'shirodhara', 'ayurvedic_facial'],
    lifestyle: 'Adaptable practices based on current imbalances'
  }
};

export { recommendations, serviceRecommendations, dualDoshaRecommendations };
